/* eslint-disable react/prop-types */
import React from 'react';

import { Header, Grid, Button, Breadcrumb, Label } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';

import {
  SEO,
  Image,
  MarkdownContent,
  OutboundLink,
  InternalLink,
  MultipleComponentIterator,
  StaffMemberSchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  EVENT_LABELS,
  PaginatedGrid,
  multipleComponentIterator,
  TeamMemberV2,
  HoverContainer,
  FishermanIcon,
  useResponsive,
  Email,
  FadeReveal,
  SlideReveal,
  PriceRange,
  Phone,
} from '@bluefin/components';

import { createTeamMemberSlug, createServiceItemSlug } from '../../index';
import {
  getPageByType,
  getBookNowPage,
  getCtaInteractions,
  getBusinessFiles,
  generateDefaultHeadshotAltText,
  getTeamMemberServices,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes } from '../../utils';

export default function TeamMemberTemplate({ data, pageContext }) {
  const {
    fishermanBusinessTeam: {
      _id,
      name,
      email,
      phone,
      description,
      role,
      gatsbyImage,
      image,
      socialMedia = [],
      locations = [],
      interactions = [],
      teams = [],
      associatedItemVariations = [],
      files = [],
      hours = [],
      seoTitle,
      seoDescription,
    },
    fishermanBusiness: { name: businessName, businessCategories = [] } = {},
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    allFishermanBusinessTeam: { nodes: allTeamMembers = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] } = {},
    allFishermanBusinessMenuCategory: { nodes: allServiceCategories = [] } = {},
    allFishermanBusinessMenuItem: { nodes: allServiceItems = [] } = {},
    site: { siteMetadata: { baseUrl } = {} },
  } = data;

  const {
    fishermanParentPageTitle = 'Team',
    fishermanParentPageSlug = '/team/',
    templateProps = {},
  } = pageContext;
  const {
    otherTeamMembersText = 'The Team Is Here For You',
    otherTeamMembersDescription = 'Your customized treatment is geared to restore, rejuvenate, and enhance your natural beauty. Our goal is to improve the appearance, skin texture, and fresh youthful appeal of your face, neck, and body.',
  } = templateProps;

  function getOtherTeamMembers() {
    return allTeamMembers.filter(({ _id: otherTeamMemberId }) => otherTeamMemberId !== _id);
  }
  const { isDesktop } = useResponsive();

  const bookingPage = getBookNowPage(allPages);
  const teamPage = getPageByType(allPages, 'Team');
  const otherTeamMembers = getOtherTeamMembers();
  const headshotSrc = gatsbyImage || image;
  const hasHeadshot = !!headshotSrc;
  const teamMemberFiles = getBusinessFiles(files, allFiles);
  const hasImages = !_isEmpty(teamMemberFiles);
  const { title: teamPageTitle, slug: teamPageSlug } = teamPage || {};
  const filteredInteractions = getCtaInteractions(interactions);
  const locationNames = (locations || []).map(({ name: locationName }) => locationName).join(', ');
  const teamMemberServices = getTeamMemberServices(allServiceCategories, associatedItemVariations);

  const HeadShot = (
    <Image
      className="headshot"
      src={headshotSrc}
      background
      altText={generateDefaultHeadshotAltText(name, businessName)}
    />
  );

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`${fishermanParentPageTitle} - ${name}`}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={hasHeadshot ? [headshotSrc] : null}
      />
      <StaffMemberSchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        jobTitle={role}
        description={description}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, ensureSlugSlashes(fishermanParentPageSlug)),
          },
          {
            name: `${fishermanParentPageTitle} - ${name} | ${businessName}`,
          },
        ]}
      />
      <div className="team-member-page-container">
        <div className={`header-section ${!hasImages ? 'no-images' : ''}`}>
          <Breadcrumb>
            <Breadcrumb.Section
              link
              to={ensureSlugSlashes(fishermanParentPageSlug)}
              as={InternalLink}
            >
              {fishermanParentPageTitle}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right chevron" />
            <Breadcrumb.Section>{name}</Breadcrumb.Section>
          </Breadcrumb>

          {hasHeadshot && !isDesktop && HeadShot}

          <Header as="h1" className="name">
            {name}
          </Header>
          <div className="role-container">
            <Header>{role}</Header>

            <div className="icons">
              {!_isEmpty(email) && <Email as="icon" email={email} />}
              {!_isEmpty(phone) && (
                <Phone phone={phone} iconName="phone" ariaLabel={`Call ${name}`} />
              )}
            </div>
          </div>

          {!_isEmpty(socialMedia) && (
            <TeamMemberV2.Social className="team-member-social" social={socialMedia} showHandle />
          )}

          {!_isEmpty(teams) && (
            <div className="teams">
              {teams.map(({ name: teamName, _id: teamId }) => (
                <Label key={teamId} className="team-label" content={teamName} />
              ))}
            </div>
          )}

          {!_isEmpty(locationNames) && (
            <div className="locations">
              <span>{locationNames}</span>
            </div>
          )}
        </div>

        <Grid stackable doubling className="team-member-info">
          <Grid.Column width={7} className="content-column">
            {hasHeadshot && isDesktop && HeadShot}

            {!_isEmpty(description) && <MarkdownContent>{description}</MarkdownContent>}

            <TeamMemberV2.Schedule hours={hours} fullDayName />

            <div className="actions">
              {!_isEmpty(bookingPage) && _isEmpty(filteredInteractions) && (
                <Button
                  primary
                  as={OutboundLink}
                  to={bookingPage.url}
                  event={{
                    category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                  }}
                >
                  {bookingPage.title}
                </Button>
              )}
              {filteredInteractions.map((interaction) => {
                const { _id: interactionId } = interaction;
                return (
                  <WebsiteComponentInteraction
                    key={interactionId}
                    interaction={interaction}
                    className="interactions"
                    displayComponentPropsMap={{
                      button: {
                        size: null,
                        compact: false,
                      },
                    }}
                    event={{
                      category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.TEAM_MEMBER_INTERACTION_ACTION,
                    }}
                  />
                );
              })}
            </div>
            {!_isEmpty(teamMemberServices) && (
              <div className="team-member-services-list">
                {teamMemberServices.map(({ name: categoryName, items }) => {
                  return (
                    <div className="team-member-service-category" key={categoryName}>
                      <Header as="h4">{categoryName}</Header>
                      {items.map((item) => {
                        const { name: itemName, variations } = item;

                        return (
                          <InternalLink
                            className="team-member-service-item"
                            key={itemName}
                            to={createServiceItemSlug(item, allServiceItems, allServiceCategories)}
                          >
                            <Header as="h5">{itemName}</Header>
                            <PriceRange
                              prices={variations.map(({ price }) => ({ amount: price }))}
                              settings={{
                                allowMixedFormats: true,
                                displayZeroCost: false,
                                displayType: 'range',
                              }}
                            />
                          </InternalLink>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </Grid.Column>

          {hasImages && (
            <Grid.Column width={9} className="images-column">
              <PaginatedGrid
                className="images-grid"
                columns={2}
                rows={2}
                tablet={{ columns: 2, rows: 2 }}
                mobile={{ columns: 1, rows: 1, stackable: false }}
                items={multipleComponentIterator({
                  iterator: teamMemberFiles,
                  components: [
                    {
                      component: <FadeReveal triggerOnce />,
                      children: [
                        {
                          component: <Image background />,
                          propMap: { src: 'src', altText: 'altText' },
                        },
                      ],
                    },
                  ],
                })}
              />
            </Grid.Column>
          )}
        </Grid>

        {!_isEmpty(otherTeamMembers) && (
          <>
            <div className="other-stylists-divider" />
            <Grid stackable doubling className="other-stylists-container">
              <Grid.Column width={6} className="content-column">
                <Header as="h2">{otherTeamMembersText}</Header>
                {!_isEmpty(otherTeamMembersDescription) && (
                  <MarkdownContent>{otherTeamMembersDescription}</MarkdownContent>
                )}
                {!_isEmpty(teamPage) && (
                  <Button
                    to={ensureSlugSlashes(teamPageSlug)}
                    primary
                    as={InternalLink}
                    event={{
                      category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                      label: EVENT_LABELS.TEAM_MEMBER_LABEL,
                    }}
                  >
                    See all {teamPageTitle}
                  </Button>
                )}
              </Grid.Column>

              <Grid.Column width={10} className="other-stylists-column">
                <Grid stackable columns={2} className="featured-stylists-grid">
                  <MultipleComponentIterator
                    iterator={otherTeamMembers}
                    limit={8}
                    components={[
                      {
                        component: (
                          <Grid.Column
                            as={InternalLink}
                            className="team-member"
                            event={{ category: 'Team', action: 'View Team Member' }}
                          />
                        ),
                        propMap: {
                          key: '_id',
                          to: createTeamMemberSlug,
                          'event.label': 'name',
                        },
                        children: [
                          {
                            component: <SlideReveal fade direction="up" triggerOnce />,
                            children: [
                              {
                                component: <HoverContainer />,
                                children: [
                                  {
                                    component: <div className="name-container" />,
                                    children: [
                                      {
                                        component: <Header />,
                                        propMap: {
                                          content: 'name',
                                        },
                                      },
                                      {
                                        component: (
                                          <HoverContainer.Content
                                            displayOnTouchScreen
                                            animation="height"
                                            animationProps={{ duration: 750, fade: true }}
                                          />
                                        ),
                                        children: [
                                          {
                                            component: <FishermanIcon iconName="arrow-right" />,
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                  {
                                    component: (
                                      <HoverContainer.Content
                                        displayOnTouchScreen={false}
                                        animation="height"
                                        animationProps={{ duration: 750, fade: true }}
                                      />
                                    ),
                                    children: [
                                      {
                                        component: <span className="role" />,
                                        propMap: {
                                          children: 'role',
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    component: (
                                      <HoverContainer.Content
                                        displayOnTouchScreen={false}
                                        animation="height"
                                        animationProps={{ duration: 750, fade: true }}
                                      />
                                    ),
                                    children: [
                                      {
                                        component: <TeamMemberV2.Social />,
                                        propMap: {
                                          social: 'socialMedia',
                                        },
                                      },
                                    ],
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ]}
                  />
                </Grid>
              </Grid.Column>
            </Grid>
          </>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusinessTeam(_id: { eq: $id }) {
      _id
      name
      email
      phone
      description
      role
      image
      gatsbyImage {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      socialMedia {
        username
        platform
        url
      }
      locations {
        _id
        name
      }
      hours {
        startDay
        openTime
        closeTime
      }
      interactions {
        behaviorType
        displayType
        enabled
        label
        url
        style
      }
      teams {
        _id
        name
      }
      associatedItemVariations {
        itemId
        item {
          _id
          name
          description
          visible
          variations {
            _id
            name
            order
            price
          }
        }
        variationId
        price
      }
      files
      seoTitle
      seoDescription
    }
    fishermanBusiness {
      name
      businessCategories {
        category
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
        slug
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessMenuCategory {
      nodes {
        _id
        items
        name
      }
    }
    allFishermanBusinessMenuItem {
      nodes {
        _id
        name
      }
    }
    allFishermanBusinessTeam(sort: { order: ASC, fields: order }, filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        role
        group
        email
        phone
        description
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        socialMedia {
          username
          platform
          url
        }
        hours {
          startDay
          openTime
          closeTime
        }
        locations {
          _id
          name
          location
          merchantLocation
        }
        show
        files
      }
    }
  }
`;
